import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { groupBy } from 'lodash/collection';
import { sumBy } from 'lodash/math';
import { Typography, Divider, Hidden } from '@material-ui/core';
import Translator from '%app/js/common/services/translator';
import ticketTypeShape from '%paysera_seat_selection_integration/js/seatings/propTypes/ticketTypeShape';
import cartItemShape from '%paysera_seat_selection_integration/js/seatings/propTypes/cartItemShape';
import SeatingCartItem from './component/SeatingCartItem';
import PlainCartItem from './component/PlainCartItem';

function CartItemList({
    seatsEnabled,
    ticketTypes,
    ticketTypeColors,
    items,
    ...rest
}) {
    const groupedItems = useMemo(
        () => groupBy(items, item => item.ticket_type.hash),
        [items],
    );

    if (seatsEnabled && items.every(item => item.seating)) {
        return Object.entries(groupedItems).map(([hash, group], key) => (
            <React.Fragment key={hash}>
                <Hidden smDown={key === 0}>
                    <Divider />
                </Hidden>
                <div>
                    <Typography variant="h6">
                        {ticketTypes[hash].name}
                    </Typography>
                    <Typography color="textSecondary" variant="subtitle2">{ticketTypes[hash].description}</Typography>
                </div>
                {sumBy(group, 'tickets_count') < ticketTypes[hash].min_tickets_count && (
                    <div>
                        <Typography color="error" variant="subtitle2">
                            {Translator.trans(
                                'seat_selection_integration.cart.ticket_min_count_notification',
                                { count: ticketTypes[hash].min_tickets_count },
                            )}
                        </Typography>
                    </div>
                )}
                {group.map(item => (
                    <SeatingCartItem
                        item={item}
                        key={item.order_detail_id}
                        ticketType={ticketTypes[hash]}
                        ticketTypeColor={ticketTypeColors[hash]}
                        {...rest}
                    />
                ))}
            </React.Fragment>
        ));
    }

    return items.map((item, key) => (
        <React.Fragment key={item.order_detail_id}>
            <PlainCartItem
                item={item}
                ticketType={ticketTypes[item.ticket_type.hash]}
                {...rest}
            />
            {key < items.length - 1 && (<Divider />)}
        </React.Fragment>
    ));
}

CartItemList.propTypes = {
    seatsEnabled: PropTypes.bool.isRequired,
    ticketTypes: PropTypes.objectOf(PropTypes.shape(ticketTypeShape)).isRequired,
    ticketTypeColors: PropTypes.objectOf(PropTypes.string).isRequired,
    items: PropTypes.arrayOf(PropTypes.shape(cartItemShape)).isRequired,
};

export default CartItemList;
